import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal } from '@heroui/react';
import { renderNotice } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { UndoFailure } from '../../importUndo';

type UndoImportDialogProps = {
    isOpen: boolean;
    /** The branch the import's stock leaves. */
    branchName: string;
    /** The import posted a book entry, which the undo reverses. */
    posted: boolean;
    isUndoing: boolean;
    /** Why the last press did not undo the import; `null` before one. */
    failure: UndoFailure | null;
    /** Keep the import: closes the dialog; ignored while the undo runs. */
    onCancel: () => void;
    /** Undo (and Try again after a failure). */
    onConfirm: () => void;
};

/**
 * Asks once before an undo: what goes (the products, variants and stock on
 * the branch; the names it created that nothing else uses) and that the
 * book entry is reversed, not deleted. Done's Finish confirm is the shape.
 *
 * A failure stays in the dialog, the Result behind it untouched. A refusal
 * is the import's sentence with Close alone, since pressing again gets the
 * same answer; no answer offers Try again.
 */
export const UndoImportDialog: React.FC<UndoImportDialogProps> = ({
    isOpen,
    branchName,
    posted,
    isUndoing,
    failure,
    onCancel,
    onConfirm,
}) => {
    const { t } = useTranslation('pages');
    const isRefused = failure?.status === 'refused';

    return (
        <Modal isOpen={isOpen} onOpenChange={(open) => !open && onCancel()}>
            <Modal.Backdrop isDismissable={!isUndoing}>
                <Modal.Container size="md">
                    <Modal.Dialog
                        className="w-full max-w-lg"
                        {...getNodeTestIdProp(
                            'catalog-import-result-undo-dialog',
                        )}
                    >
                        <Modal.CloseTrigger />
                        <Modal.Header>
                            <Modal.Heading>
                                {t('catalogImport.result.undo.confirm.title')}
                            </Modal.Heading>
                        </Modal.Header>
                        <Modal.Body className="flex flex-col gap-4">
                            <ul className="flex list-disc flex-col gap-1 ps-5 text-sm text-muted">
                                <li>
                                    {t(
                                        'catalogImport.result.undo.confirm.stock',
                                        { branch: branchName },
                                    )}
                                </li>
                                <li>
                                    {t(
                                        'catalogImport.result.undo.confirm.names',
                                    )}
                                </li>
                                {posted ? (
                                    <li
                                        {...getNodeTestIdProp(
                                            'catalog-import-result-undo-books',
                                        )}
                                    >
                                        {t(
                                            'catalogImport.result.undo.confirm.books',
                                        )}
                                    </li>
                                ) : null}
                            </ul>
                            {failure ? (
                                <Alert
                                    status="danger"
                                    {...getNodeTestIdProp(
                                        'catalog-import-result-undo-error',
                                    )}
                                >
                                    <Alert.Indicator />
                                    <Alert.Content>
                                        <Alert.Title>
                                            {t(
                                                'catalogImport.result.undo.confirm.failedTitle',
                                            )}
                                        </Alert.Title>
                                        <Alert.Description>
                                            {failure.status === 'refused'
                                                ? renderNotice(
                                                      t,
                                                      failure.notice,
                                                  )
                                                : t(
                                                      'catalogImport.result.undo.confirm.noAnswer',
                                                  )}
                                        </Alert.Description>
                                    </Alert.Content>
                                </Alert>
                            ) : null}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                isDisabled={isUndoing}
                                onPress={onCancel}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'catalog-import-result-undo-cancel',
                                )}
                            >
                                {isRefused
                                    ? t(
                                          'catalogImport.result.undo.confirm.close',
                                      )
                                    : t(
                                          'catalogImport.result.undo.confirm.keep',
                                      )}
                            </Button>
                            {isRefused ? null : (
                                <Button
                                    isPending={isUndoing}
                                    onPress={onConfirm}
                                    variant="danger"
                                    {...getNodeTestIdProp(
                                        'catalog-import-result-undo-confirm',
                                    )}
                                >
                                    {failure
                                        ? t(
                                              'catalogImport.result.undo.confirm.retry',
                                          )
                                        : t(
                                              'catalogImport.result.undo.confirm.undo',
                                          )}
                                </Button>
                            )}
                        </Modal.Footer>
                    </Modal.Dialog>
                </Modal.Container>
            </Modal.Backdrop>
        </Modal>
    );
};
